'use client'

import { useRef } from 'react'

import { CSSTransition } from 'react-transition-group'

import { AuthProvider, useAuth, AUTH_TYPES } from './AuthContext'
import LoginForm from './LoginForm'
import RegisterForm from './RegisterForm'
import VerifyForm from './VerifyForm'
import ForgottenPasswordForm from './ForgottenPasswordForm'
import MFAForm from './MFAForm'
import MFASelectForm from './MFASelectForm'
import OAuthConsentForm from './OAuthConsentForm'

import Logo from '@/images/logo.svg'

import styles from './Auth.module.scss'
import classNames from 'classnames/bind'
const classes = classNames.bind(styles)

function AuthLayout() {
    const { selectedType, error, mfaSession, showVerification } = useAuth()

    const nodeRefs = {
        login: useRef(null),
        register: useRef(null),
        reset: useRef(null),
        verification: useRef(null),
        mfa: useRef(null),
        mfaSelect: useRef(null),
        oauthConsent: useRef(null),
    }

    const transitionClassNames = {
        enter: classes('enter'),
        enterActive: classes('enter-active'),
        exit: classes('exit'),
        exitActive: classes('exit-active'),
    }

    const ErrorMessage = () =>
        error &&
        error.length > 0 && (
            <div className={classes('error-container')}>
                {error.map((err, index) => (
                    <p key={index}>{err}</p>
                ))}
            </div>
        )

    const isFormVisible = (formType) => {
        const isMfa = mfaSession !== null && selectedType === AUTH_TYPES.MFA_VERIFY
        const isMfaSelect = mfaSession !== null && selectedType === AUTH_TYPES.MFA_SELECT_TYPE
        const isOAuthConsent = selectedType === AUTH_TYPES.OAUTH_CONSENT
        if (showVerification || isMfa || isMfaSelect || isOAuthConsent) return false
        return selectedType === formType
    }

    return (
        <div className={classes('wrapper')}>
            <Logo className={classes('logo')} />
            <div className={classes('graphic-wrapper')} />
            <div className={classes('form-wrapper')}>
                <CSSTransition
                    in={isFormVisible(AUTH_TYPES.LOGIN)}
                    timeout={200}
                    classNames={transitionClassNames}
                    unmountOnExit
                    nodeRef={nodeRefs.login}
                >
                    <div className={classes('transition-wrap')} ref={nodeRefs.login}>
                        <div className={classes('container')}>
                            <LoginForm />
                            <ErrorMessage />
                        </div>
                    </div>
                </CSSTransition>

                <CSSTransition
                    in={isFormVisible(AUTH_TYPES.REGISTER)}
                    timeout={200}
                    classNames={transitionClassNames}
                    unmountOnExit
                    nodeRef={nodeRefs.register}
                >
                    <div className={classes('transition-wrap')} ref={nodeRefs.register}>
                        <div className={classes('container')}>
                            <RegisterForm />
                            <ErrorMessage />
                        </div>
                    </div>
                </CSSTransition>

                <CSSTransition
                    in={isFormVisible(AUTH_TYPES.PASSWORD_RESET)}
                    timeout={200}
                    classNames={transitionClassNames}
                    unmountOnExit
                    nodeRef={nodeRefs.reset}
                >
                    <div className={classes('transition-wrap')} ref={nodeRefs.reset}>
                        <div className={classes('container')}>
                            <ForgottenPasswordForm />
                            <ErrorMessage />
                        </div>
                    </div>
                </CSSTransition>

                <CSSTransition
                    in={showVerification}
                    timeout={300}
                    classNames={transitionClassNames}
                    unmountOnExit
                    nodeRef={nodeRefs.verification}
                >
                    <div className={classes('transition-wrap')} ref={nodeRefs.verification}>
                        <div className={classes('container')}>
                            <VerifyForm />
                            <ErrorMessage />
                        </div>
                    </div>
                </CSSTransition>

                <CSSTransition
                    in={selectedType === AUTH_TYPES.MFA_SELECT_TYPE}
                    timeout={300}
                    classNames={transitionClassNames}
                    unmountOnExit
                    nodeRef={nodeRefs.mfaSelect}
                >
                    <div className={classes('transition-wrap')} ref={nodeRefs.mfaSelect}>
                        <div className={classes('container')}>
                            <MFASelectForm />
                            <ErrorMessage />
                        </div>
                    </div>
                </CSSTransition>

                <CSSTransition
                    in={selectedType === AUTH_TYPES.MFA_VERIFY}
                    timeout={300}
                    classNames={transitionClassNames}
                    unmountOnExit
                    nodeRef={nodeRefs.mfa}
                >
                    <div className={classes('transition-wrap')} ref={nodeRefs.mfa}>
                        <div className={classes('container')}>
                            <MFAForm />
                            <ErrorMessage />
                        </div>
                    </div>
                </CSSTransition>

                <CSSTransition
                    in={selectedType === AUTH_TYPES.OAUTH_CONSENT}
                    timeout={300}
                    classNames={transitionClassNames}
                    unmountOnExit
                    nodeRef={nodeRefs.oauthConsent}
                >
                    <div className={classes('transition-wrap')} ref={nodeRefs.oauthConsent}>
                        <div className={classes('container')}>
                            <OAuthConsentForm />
                            <ErrorMessage />
                        </div>
                    </div>
                </CSSTransition>
            </div>
        </div>
    )
}

export default function Auth({ type, isOAuthPage = false, clientData = null, skipToConsent = false }) {
    return (
        <AuthProvider
            initialType={type}
            isOAuthPage={isOAuthPage}
            clientData={clientData}
            skipToConsent={skipToConsent}
        >
            <AuthLayout />
        </AuthProvider>
    )
}
